import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActionIconsCont,
  AddParticipantIcon,
  AddTeamReportIcon,
  DeleteReportIcon,
  EditReportIcon,
  ExecutiveTileButtonsCont,
  LeftDetailsCont,
  Name,
  ParticipantsCount,
  ResizeCont,
  RolesCount,
  StatisticsCont,
  TileCont,
  Type,
  TypeCont,
  TypeCell,
} from './ReportsSetupTableTile.styles';
import TableTile from '../../../HOC/TableTile/TableTile';
import { useReportsSetup } from '../../../hooks';
import { useFilters } from '../../../hooks/useFilters';
import { IReportsSetupItem } from '../../../services/surveys';
import ReportsSetupParticipants from '../../ReportsSetupParticipants/ReportsSetupParticipants';
import { PARTICIPANTS_CELL_WIDTH } from '../ReportsSetupTableHeader/ReportsSetupTableHeader.styles';
import CloseIcon from '@/assets/icons/closeAllAccordion.svg?react';
import OpenIcon from '@/assets/icons/openAllAccordion.svg?react';

interface IReportsSetupTableTile {
  name: string;
  type: 'team' | 'executive';
  id: string;
  memberCount: {
    participants?: number;
    groups?: number;
    superordinate?: number;
    member?: number;
    subordinate?: number;
    leader?: number;
  };
  searchMembers?: IReportsSetupItem[];
  deleteItem: (item: IReportsSetupItem) => void;
  editItem: (item: IReportsSetupItem) => void;
  updateFullTable: () => Promise<void>;
  isEffectSurvey: boolean;
}

const ReportsSetupTableTile: FC<IReportsSetupTableTile> = ({
  name,
  type,
  id,
  memberCount,
  searchMembers,
  deleteItem,
  editItem,
  updateFullTable,
  isEffectSurvey,
}) => {
  const { getTypeStyle } = useReportsSetup();
  const { getNestedFilters, applyNestedFilter } = useFilters();
  const { t } = useTranslation();

  const [formToAddVisibility, setFormToAddVisibility] = useState(false);
  const [isInitiallyActive, setIsInitiallyActive] = useState(false);

  const reportsKey = 'reports';
  const reportsTileFilters = getNestedFilters(reportsKey);
  const active = reportsTileFilters[id]?.active || false;
  const { backgroundColor, borderColor, marginRight } = getTypeStyle(type);
  const hasSearchResults = searchMembers && searchMembers.length > 0;

  const resizeReport = () => {
    const isActive = !active;
    const conditionFilters = isActive ? { perPage: '5' } : { perPage: null };
    const filters = { active: isActive, ...conditionFilters };

    if (isInitiallyActive) {
      setIsInitiallyActive(false);
    }

    applyNestedFilter(filters, id, reportsKey);
    if (!isActive) {
      setFormToAddVisibility(false);
    }
  };

  const addNewParticipant = () => {
    if (!active) {
      resizeReport();
    }
    if (!formToAddVisibility) {
      setFormToAddVisibility(true);
    }
  };

  useEffect(() => {
    if (hasSearchResults) {
      setIsInitiallyActive(true);
    }
  }, [hasSearchResults]);

  useEffect(() => {
    if (isInitiallyActive && !active) {
      applyNestedFilter({ active: !active }, id, reportsKey);
    }
  }, [active, id, isInitiallyActive, applyNestedFilter]);

  return (
    <TableTile
      isShadowed={true}
      active={active}
      body={
        <ReportsSetupParticipants
          type={type}
          groupId={id}
          setActiveForm={setFormToAddVisibility}
          activeForm={formToAddVisibility}
          updateFullTable={updateFullTable}
          hasSearchResults={hasSearchResults}
          isEffectSurvey={isEffectSurvey}
        />
      }
    >
      <TileCont id="TileCont">
        <ResizeCont onClick={resizeReport} id="ResizeCont" data-testid="resizeCont">
          {active ? <CloseIcon id="CloseIcon" /> : <OpenIcon id="OpenIcon" />}
        </ResizeCont>
        <LeftDetailsCont id="LeftDetailsCont">
          <Name id="Name">{name}</Name>
          {isEffectSurvey ? null : (
            <TypeCell>
              <TypeCont
                id="TypeCont"
                backgroundColor={backgroundColor}
                borderColor={borderColor}
                marginRight={marginRight}
              >
                <Type id="Type">{type}</Type>
              </TypeCont>
            </TypeCell>
          )}

          {isEffectSurvey ? (
            <div style={{ display: 'flex', width: PARTICIPANTS_CELL_WIDTH }}>
              <RolesCount id="EmployeesCount">
                <span>{memberCount?.subordinate ?? 0} </span>
                <span>
                  {t(
                    `viewSurvey.reportsSetup.participants.additional.subordinate${
                      (memberCount?.subordinate ?? 0) > 1 ? 's' : ''
                    }`
                  )}
                </span>
              </RolesCount>
              <RolesCount id="ManagersCount">
                <span>{memberCount?.member ?? 0} </span>
                <span style={{ width: '120px' }}>
                  {t(
                    `viewSurvey.reportsSetup.participants.additional.member${
                      (memberCount?.member ?? 0) > 1 ? 's' : ''
                    }`
                  )}
                </span>
              </RolesCount>
              <RolesCount id="DirectorsCount">
                <span>{memberCount?.superordinate ?? 0} </span>
                <span>
                  {t(
                    `viewSurvey.reportsSetup.participants.additional.superordinate${
                      (memberCount?.superordinate ?? 0) > 1 ? 's' : ''
                    }`
                  )}
                </span>
              </RolesCount>
              <RolesCount id="TeamledersCount">
                <span>{memberCount?.leader ?? 0} </span>
                <span style={{ width: '120px' }}>
                  {t(
                    `viewSurvey.reportsSetup.participants.additional.leader${
                      (memberCount?.leader ?? 0) > 1 ? 's' : ''
                    }`
                  )}
                </span>
              </RolesCount>
            </div>
          ) : (
            <>
              {type === 'team' ? (
                <ParticipantsCount id="ParticipantsCount">
                  {memberCount.participants || 0}{' '}
                  {t('viewSurvey.reportsSetup.participants.participants')}
                </ParticipantsCount>
              ) : (
                <StatisticsCont>
                  <ParticipantsCount id="ParticipantsCount">
                    {memberCount?.groups || 0}{' '}
                    {t('viewSurvey.reportsSetup.participants.TeamReports')}
                  </ParticipantsCount>
                  <ParticipantsCount id="ParticipantsCount">
                    {memberCount.participants || 0}{' '}
                    {t('viewSurvey.reportsSetup.participants.participants')}
                  </ParticipantsCount>
                </StatisticsCont>
              )}
            </>
          )}
        </LeftDetailsCont>
        <ActionIconsCont id="ActionIcons" isVisible={active}>
          {isEffectSurvey ? null : (
            <>
              {type !== 'executive' ? (
                <AddParticipantIcon id="AddParticipantIcon" onClick={addNewParticipant} />
              ) : (
                <AddTeamReportIcon id="AddTeamReport" onClick={addNewParticipant} />
              )}
            </>
          )}
          <ExecutiveTileButtonsCont>
            <EditReportIcon
              id="EditReport"
              onClick={() => editItem({ id, memberCount, name, type })}
            />
            <DeleteReportIcon
              id="DeleteReport"
              onClick={() => deleteItem({ id, memberCount, name, type })}
            />
          </ExecutiveTileButtonsCont>
        </ActionIconsCont>
      </TileCont>
    </TableTile>
  );
};

export default ReportsSetupTableTile;
